import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { fetchVideoById, incrementViewCount } from '../../services/videoApi';
import { recordWatch, fetchWatchRecordByVideoId } from '../../services/watchRecordApi';
import {
  likeVideo,
  unlikeVideo,
  getVideoLikesCount,
  getVideoLikers
} from '../../services/videoLikeApi';
import { addFavorite, removeFavorite } from '../../services/favoriteApi';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { useDownloadProgress } from '../../contexts/DownloadProgressProvider';
import { SnackbarType } from '../../components/Snackbar/types';
import { toRelativePath } from '../../utils/videoImagePath';
import { UseVideoDetailReturn, VideoMetadata, Video, ProgressState } from './types';
import { User } from '../../components/VideoDetailLikesModal/types';
import { saveSelection, restoreSelection } from '../../services/selectionUtils';
import { SessionManager } from '../../utils/SessionManager';
import { downloadVideo } from '../../utils/videoDownload';

const WATCH_RECORD_UPDATE_INTERVAL_SECONDS = 10;

export const useVideoDetail = (): UseVideoDetailReturn => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { startDownloadProgress, updateDownloadProgress, completeDownloadProgress } = useDownloadProgress();

  // 基本状態
  const [video, setVideo] = useState<Video | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  
  // 関連動画に必要な情報のみを保持するref
  const videoMetadataRef = useRef<VideoMetadata | null>(null);
  
  // 再生位置管理
  const [lastWatchSeconds, setLastWatchSeconds] = useState(0);
  const playerRef = useRef<any>(null);
  const [duration, setDuration] = useState(0);

  // 視聴時間管理
  const accumulatedTimeRef = useRef(0);
  const lastTimeRef = useRef(0);
  const lastWatchRecordTimeRef = useRef(0);
  const [viewCounted, setViewCounted] = useState(false);
  const updateInProgressRef = useRef(false);
  const pendingWatchRecordFlushRef = useRef(false);

  // いいね関連
  const [hasLiked, setHasLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  // お気に入り
  const [hasFavorited, setHasFavorited] = useState(false);

  // いいねした人一覧
  const [likers, setLikers] = useState<User[]>([]);
  const [likeDialogOpen, setLikeDialogOpen] = useState(false);

  // 音声関連の状態 - 初回は音声ありで試行
  const [isMuted, setIsMuted] = useState(false);
  
  // いま表示しているプレイヤーに張った自動再生のやり直し（タイマー・リスナー・待ち中の play()）を止める関数
  const playerSessionCleanupRef = useRef<(() => void) | null>(null);

  // 通知フラグ（複数回通知を防止）
  const hasShownAutoPlayErrorRef = useRef(false);
  
  // 自動再生エラー処理
  const handleAutoPlayError = (error: Error) => {
    // 既に通知済みの場合は表示しない
    if (hasShownAutoPlayErrorRef.current) {
      return;
    }
    
    showSnackbar(
      'ブラウザの設定により音声付き自動再生が制限されています。ミュート状態で再生します。',
      SnackbarType.INFO,
      6000 // 長めの表示時間
    );
    
    // ミュート状態を設定
    setIsMuted(true);
    
    // 通知済みフラグを立てる
    hasShownAutoPlayErrorRef.current = true;
  };
  
  // 音声切り替え関数
  const handleToggleMute = () => {
    // プレイヤーのミュート状態を切り替える
    if (playerRef.current) {
      const player = playerRef.current;
      const internalPlayer = player.getInternalPlayer();
      if (internalPlayer) {
        (internalPlayer as HTMLVideoElement).muted = !isMuted;
      }
    }
    // 状態を更新
    setIsMuted(!isMuted);
  };

  // 関連動画へ移ったら（id が変わったら）、前の動画の自動再生のやり直しを止める。
  // 前のプレイヤーはこのあと dispose されるので、残ったタイマーや play() の reject が
  // 次の動画のミュート状態を書き換えたり、片付け済みの要素を再生したりしないようにする
  useEffect(() => {
    return () => {
      if (playerSessionCleanupRef.current) {
        playerSessionCleanupRef.current();
        playerSessionCleanupRef.current = null;
      }
    };
  }, [id]);

  // 関連動画用のメタデータを更新 (ビデオIDや分類情報に変更があった場合のみ)
  useEffect(() => {
    if (video && (!videoMetadataRef.current || videoMetadataRef.current.id !== video.id)) {
      videoMetadataRef.current = {
        id: video.id,
        title: video.title,
        tags: video.tags,
        categoryId: video.category_id
      };
    }
  }, [video]);

  //─────────────────────────────────────────────────────────
  // ① 初期データ取得: 動画詳細 + 視聴記録 + いいね数
  //─────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!id) return;

        // 動画詳細取得
        const v = await fetchVideoById(id);
        setVideo(v as Video);

        // 処理中のセッションの場合
        if (v.is_processing) {
          setIsConverting(true);
          // 処理中の動画は視聴記録やいいね数は取得しない
          setLastWatchSeconds(0);
          accumulatedTimeRef.current = 0;
          setLikesCount(0);
          setHasLiked(false);
          setHasFavorited(false);
          return;
        }

        // 論理削除されているかチェック
        if (v.deleted_at) {
          setIsDeleted(true);
        }

        // サムネイルが生成されていない場合でも再生は許可（B-5）
        // isConverting は is_processing の場合のみ true にする

        // 視聴記録といいね数を並列取得
        const [recordResult, likeResult] = await Promise.allSettled([
          fetchWatchRecordByVideoId(id),
          getVideoLikesCount(id)
        ]);

        // rejected の結果をログ出力
        if (recordResult.status === 'rejected') {
          console.error('視聴記録の取得に失敗:', recordResult.reason);
        }
        if (likeResult.status === 'rejected') {
          console.error('いいね数の取得に失敗:', likeResult.reason);
        }

        // 視聴記録の処理
        if (recordResult.status === 'fulfilled') {
          const record = recordResult.value;
          if (record && record.watch_seconds !== undefined) {
            const progressPct = Number(record.progress_percent) || 0;
            if (progressPct >= 95) {
              // ほぼ完走済み → 最初から再生
              setLastWatchSeconds(0);
              accumulatedTimeRef.current = 0;
            } else {
              // 途中まで → 続きから再生
              setLastWatchSeconds(record.watch_seconds);
              accumulatedTimeRef.current = record.watch_seconds;
            }
          } else {
            setLastWatchSeconds(0);
            accumulatedTimeRef.current = 0;
          }
        } else {
          setLastWatchSeconds(0);
          accumulatedTimeRef.current = 0;
        }

        // いいね数の処理
        if (likeResult.status === 'fulfilled') {
          const likeRes = likeResult.value;
          if (likeRes && likeRes.likesCount !== undefined) {
            setLikesCount(likeRes.likesCount);
          } else {
            setLikesCount(0);
          }
        } else {
          setLikesCount(0);
        }

        // 既にいいね済みか + お気に入り済みか
        setHasLiked(v.is_liked ? true : false);
        setHasFavorited(v.is_favorited ? true : false);
      } catch (err) {
        console.error('動画取得エラー:', err);
        setError('動画の読み込みに失敗しました。');
        showSnackbar('動画が見つかりませんでした。', SnackbarType.ERROR);
        navigate('/', { replace: true }); // エラー時はホームに戻す
      }
    };

    // 動画が変更された場合はonReadyCalledRefをリセット
    onReadyCalledRef.current = false;

    loadData();
  }, [id, navigate, showSnackbar]);

  //─────────────────────────────────────────────────────────
  // ② サムネイル変換チェック (一定間隔)
  //─────────────────────────────────────────────────────────
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (video && !video.thumbnail && !isDeleted && id) {
      timer = setInterval(async () => {
        try {
          const updated = await fetchVideoById(id);
          if (updated.thumbnail) {
            setVideo(updated as Video);
            setIsConverting(false);
            clearInterval(timer);
          }
        } catch (err) {
          showSnackbar('サムネイルの変換に失敗しました。', SnackbarType.ERROR);
        }
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [video, id, isDeleted, showSnackbar]);

  //─────────────────────────────────────────────────────────
  // ③ プレイヤー操作ハンドラ
  //─────────────────────────────────────────────────────────
  const onReadyCalledRef = useRef(false);
  const handlePlayerReady = (player: any) => {
    if (onReadyCalledRef.current) {
      return;
    }
    onReadyCalledRef.current = true;

    // playerRefを設定（これが欠けていたため他の関数でplayerRef.currentがnullになっていた）
    playerRef.current = player;

    // 音声関連の設定（追加）
    if (player && typeof player.getInternalPlayer === 'function') {
      const internalPlayer = player.getInternalPlayer();
      if (!internalPlayer || !internalPlayer.vjsPlayer) {
        console.warn('Internal player or vjsPlayer not available');
        return;
      }
      
      // VideoJS playerからHTMLVideoElementを取得
      const videoElement = internalPlayer.vjsPlayer.el()?.querySelector('video');
      if (!videoElement || typeof videoElement.addEventListener !== 'function') {
        console.warn('Video element not found or not a valid HTMLVideoElement');
        return;
      }
      
      // 前のプレイヤーのやり直しが残っていれば止めてから、このプレイヤーの分を張る
      if (playerSessionCleanupRef.current) {
        playerSessionCleanupRef.current();
      }
      let isSessionActive = true;
      const sessionTimers: ReturnType<typeof setTimeout>[] = [];
      const userInteractions = ['click', 'keydown', 'touchstart', 'pointerdown'];
      // ボリューム変更を状態に同期するリスナー
      const handleVolumeChange = () => {
        if (videoElement.muted !== isMuted) {
          setIsMuted(videoElement.muted);
        }
      };
      // ユーザー操作のあとに再生をやり直すリスナー（一度だけ実行する）
      const handleUserInteraction = () => {
        // ユーザーインタラクション後の自動再生を試みる（必要に応じて）
        if (videoElement.paused) {
          videoElement.play().catch((error: any) => {
            // 関連動画のクリックそのものがここに来るため、reject は移動後に届きうる
            if (!isSessionActive) return;
            console.warn('ユーザーインタラクション後の再生エラー:', error);
            if (videoElement.paused && !videoElement.muted) {
              videoElement.muted = true;
              setIsMuted(true);
              videoElement.play().catch((e: any) => console.error('ミュート再生でも失敗:', e));
            }
          });
        }
        
        // すべてのイベントリスナーを削除（一度だけ実行するため）
        userInteractions.forEach(event => {
          document.removeEventListener(event, handleUserInteraction);
        });
      };
      playerSessionCleanupRef.current = () => {
        isSessionActive = false;
        sessionTimers.forEach(timer => clearTimeout(timer));
        videoElement.removeEventListener('volumechange', handleVolumeChange);
        userInteractions.forEach(event => {
          document.removeEventListener(event, handleUserInteraction);
        });
      };

      // 初期ミュート状態を適用（ただし自動再生エラーによる強制ミュートの可能性あり）
      videoElement.muted = isMuted;
      
      
      // 再生を一時停止してからシーク処理を行う
      if (lastWatchSeconds > 0 && playerRef.current) {
        // シーク前に一時停止
        videoElement.pause();
        
        playerRef.current.seekTo(lastWatchSeconds, 'seconds');
        
        // シーク完了後に再生を再開
        sessionTimers.push(setTimeout(() => {
          // 自動再生ポリシーによる制限に対応
          videoElement.play().catch((error: any) => {
            // 待っている間に別の動画へ移った場合、この reject は前の動画のもの
            if (!isSessionActive) return;
            console.warn('自動再生エラー:', error);
            
            // 自動再生エラーハンドラを呼び出し（Snackbar通知用）
            handleAutoPlayError(error);
            
            // 必要に応じてミュート状態で再生を試みる
            if (videoElement.paused && !videoElement.muted) {
              videoElement.muted = true;
              setIsMuted(true);
              videoElement.play().catch((e: any) => console.error('ミュート再生でも失敗:', e));
            }
          });
        }, 100));
      } else {
        // 視聴位置がない場合は通常の自動再生処理
        // 自動再生ポリシーによる制限に対応
        // プレイヤーでエラーが発生した場合に備える
        const handlePlayError = (error: Error) => {
          // 待っている間に別の動画へ移った場合、この reject は前の動画のもの
          if (!isSessionActive) return;
          console.warn('自動再生エラー:', error);
          
          // 自動再生エラーハンドラを呼び出し（Snackbar通知用）
          handleAutoPlayError(error);
          
          // 必要に応じてミュート状態で再生を試みる
          if (videoElement.paused && !videoElement.muted) {
            videoElement.muted = true;
            videoElement.play().catch((e: any) => console.error('ミュート再生でも失敗:', e));
          }
        };
        
        // 再生状態を監視
        if (videoElement.paused && !isMuted) {
          // 音声付きで自動再生を試みる
          videoElement.play().catch(handlePlayError);
        }
      }
      
      // ボリューム変更イベントをリッスンして状態を同期
      videoElement.addEventListener('volumechange', handleVolumeChange);
      
      // ユーザーインタラクションを監視
      userInteractions.forEach(event => {
        document.addEventListener(event, handleUserInteraction, { once: true });
      });

      // 3秒後にイベントリスナーをクリーンアップ（ユーザーが操作しなかった場合）
      sessionTimers.push(setTimeout(() => {
        userInteractions.forEach(event => {
          document.removeEventListener(event, handleUserInteraction);
        });
      }, 3000));
    }
  };

  const handleDuration = (dur: number) => {
    setDuration(dur);
  };

  const handleProgress = (state: ProgressState) => {
    const currentTime = state.playedSeconds;
    const delta = currentTime - lastTimeRef.current;

    // シーク操作の検出（大きな時間変化があった場合）
    if (Math.abs(delta) >= 3) {
      // シーク後の位置を新しい基準点として設定
      lastTimeRef.current = currentTime;
      
      // シーク操作の後、視聴記録を即時更新
      if (currentTime > 0) {
        // 前進シークの場合のみ累積時間を更新
        // 後退シークの場合は累積時間を更新しない（既に見た部分）
        if (currentTime > accumulatedTimeRef.current) {
          accumulatedTimeRef.current = currentTime;
        }
        
        // シーク操作後は常に視聴記録を更新（前進も後退も）
        updateWatchRecord();
      }
      return; // シーク操作の場合は以降の処理をスキップ
    }

    // 通常の再生進行（小さな時間変化）
    if (delta > 0) {
      accumulatedTimeRef.current += delta;
    }
    
    lastTimeRef.current = currentTime;

    // 動画の1/10視聴で再生回数加算（プレイヤーから動的にdurationを取得）
    const currentDuration = playerRef.current && typeof playerRef.current.getDuration === 'function' 
      ? playerRef.current.getDuration() 
      : duration;
    
    if (!viewCounted && currentDuration > 0 && accumulatedTimeRef.current >= currentDuration / 10) {
      doIncrementViewCount();
    }

    // 通常再生は10秒ごとに視聴記録更新
    if (currentTime - lastWatchRecordTimeRef.current >= WATCH_RECORD_UPDATE_INTERVAL_SECONDS) {
      lastWatchRecordTimeRef.current = currentTime;
      updateWatchRecord();
    }
  };

  const handleEnded = () => {    
    
    // プレイヤーから動的にdurationを取得
    const currentDuration = playerRef.current && typeof playerRef.current.getDuration === 'function' 
      ? playerRef.current.getDuration() 
      : duration;
    
    // 累積時間を動画の長さに合わせる（完全視聴扱い）
    if (currentDuration > 0) {
      accumulatedTimeRef.current = currentDuration;
      lastTimeRef.current = currentDuration;
    }
    
    updateWatchRecord();
    if (!viewCounted) {
      doIncrementViewCount();
    }
  };

  // 再生回数インクリメント
  const doIncrementViewCount = async () => {
    if (!id) return;

    if (!sessionStorage.getItem(`viewCounted_${id}`)) {
      try {
        const updatedVideo = await incrementViewCount(id);
        // video state を更新
        setVideo((prevVideo) => {
          if (!prevVideo) return null;
          return {
            ...prevVideo,
            views: updatedVideo.views
          };
        });
        sessionStorage.setItem(`viewCounted_${id}`, 'true');
        setViewCounted(true);
      } catch (err) {
        showSnackbar('再生回数の加算に失敗しました。', SnackbarType.ERROR);
      }
    } else {
      setViewCounted(true);
    }
  };

  // 視聴記録更新
  const updateWatchRecord = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    // プレイヤーから直接duration値を取得（状態更新の遅延問題を回避）
    let currentDuration = duration; // デフォルト値として状態の値を使用
    
    if (playerRef.current) {
      
      if (typeof playerRef.current.getDuration === 'function') {
        try {
          const playerDuration = playerRef.current.getDuration();
          currentDuration = playerDuration || duration;
        } catch (error) {
          console.warn('updateWatchRecord: getDuration() failed:', error);
          currentDuration = duration;
        }
      } else {
        console.warn('updateWatchRecord: getDuration method not found on playerRef.current');
      }
    } else {
      console.warn('updateWatchRecord: playerRef.current is null/undefined');
      return;
    }


    if (!currentDuration || !video || !id) {
      return;
    }
    if (updateInProgressRef.current) {
      pendingWatchRecordFlushRef.current = true;
      return;
    }
    updateInProgressRef.current = true;

    // 現在の実際の再生位置を取得
    const currentPlayerPosition = playerRef.current ? playerRef.current.getCurrentTime() : 0;
    
    // 最大到達位置は別のフィールドとして記録（オプション）
    const maxPosition = Math.max(accumulatedTimeRef.current, currentPlayerPosition);
    
    // 最後の視聴位置を記録する（現在のプレーヤー位置）
    const lastWatchPosition = currentPlayerPosition;
    
    // 進捗率は最大位置から計算（プレイヤーから取得したdurationを使用）
    const progressPercent = ((maxPosition / currentDuration) * 100).toFixed(2);


    try {
      // 進捗率が100%に近い場合は完了とみなす
      const isCompleted = parseFloat(progressPercent) >= 95;
      
      // 視聴記録APIのリクエストデータ作成
      const watchData = {
        video_id: String(video.id),
        watch_seconds: Math.floor(lastWatchPosition),
        progress_percent: parseFloat(progressPercent),
        max_position: Math.floor(maxPosition)
      };

      
      // API呼び出し
      const result = await recordWatch(watchData);
    } catch (err: unknown) {
      // エラーの詳細をログに出力
      console.error('視聴記録更新エラー:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        console.warn('認証情報が不足しているため視聴記録を保存できませんでした');
        if (!silent) {
          showSnackbar('視聴記録の保存にはログインが必要です', SnackbarType.WARNING);
        }
      } else if (!silent) {
        // その他のエラー
        showSnackbar('視聴記録の更新に失敗しました。', SnackbarType.ERROR);
      }
    } finally {
      updateInProgressRef.current = false;
      if (pendingWatchRecordFlushRef.current) {
        pendingWatchRecordFlushRef.current = false;
        updateWatchRecord({ silent: true });
      }
    }
  }, [duration, video, id, showSnackbar]);

  // タブ非表示・ページ離脱時に視聴記録をフラッシュ
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateWatchRecord({ silent: true });
      }
    };
    const handlePageHide = () => {
      updateWatchRecord({ silent: true });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      updateWatchRecord({ silent: true });
    };
  }, [updateWatchRecord]);

  //─────────────────────────────────────────────────────────
  // ④ その他のUI操作 (タグクリック, いいね, お気に入りなど)
  //─────────────────────────────────────────────────────────
  const handleTagClick = (tagValue: string) => {
    const trimmed = tagValue.trim();
    if (trimmed) {
      navigate(`/?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const handleLike = async () => {
    if (!id) return { success: false };
    
    try {
      if (hasLiked) {
        await unlikeVideo(id);
        setHasLiked(false);
        setLikesCount((prev) => Math.max(prev - 1, 0));
        return { success: true };
      } else {
        await likeVideo(id);
        setHasLiked(true);
        setLikesCount((prev) => prev + 1);
        return { success: true };
      }
    } catch (err) {
      // ここではメッセージを表示せず、エラーを再スローする
      throw err;
    }
  };

  const handleFavorite = async () => {
    if (!id) return { success: false };
    
    try {
      if (hasFavorited) {
        await removeFavorite(id);
        setHasFavorited(false);
        return { success: true };
      } else {
        await addFavorite({ video_id: id.toString() });
        setHasFavorited(true);
        return { success: true };
      }
    } catch (err) {
      // ここではメッセージを表示せず、エラーを再スローする
      throw err;
    }
  };

  // いいねした人一覧表示
  const handleShowLikers = async () => {
    if (!id) return { success: false };
    
    try {
      const res = await getVideoLikers(id);
      setLikers(res.likers || []);
      setLikeDialogOpen(true);
      return { success: true };
    } catch (err) {
      // ここではメッセージを表示せず、エラーを再スローする
      throw err;
    }
  };
  const closeLikersDialog = () => setLikeDialogOpen(false);

  // コメント関連（ページ内リンク）
  const handleComment = () => {
    const cSection = document.getElementById('comments-section');
    if (cSection) {
      // スクロールの量を調整：コメントセクションの少し上の位置で止まるようにする
      const rect = cSection.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - 80; // 80pxほど上に調整
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  };
  const handleCommentWithTime = () => {
    const cSection = document.getElementById('comments-section');
    if (cSection) {
      // スクロールの量を調整：コメントセクションの少し上の位置で止まるようにする
      const rect = cSection.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - 80; // 80pxほど上に調整
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    if (playerRef.current) {
      const currentTime = Math.floor(playerRef.current.getCurrentTime());
      const mm = String(Math.floor(currentTime / 60)).padStart(2, '0');
      const ss = String(currentTime % 60).padStart(2, '0');
      const commentDiv = document.getElementById('comment-input');
      if (commentDiv) {
        commentDiv.focus();
        // contentEditableのdiv要素にテキストを挿入（Selection API使用）
        const editorDiv = commentDiv as HTMLDivElement;
        if (editorDiv) {
          // テキスト挿入前のカーソル位置を文字列位置として保存
          const savedSelection = saveSelection(editorDiv);
          const currentText = editorDiv.innerText || '';
          const currentCursorPosition = savedSelection ? savedSelection.start : currentText.length;
          
          // タイムスタンプのテキスト
          const timestampText = ` #${mm}:${ss} `;
          
          // タイムスタンプを挿入
          const selection = window.getSelection();
          const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
          
          if (range && (range.commonAncestorContainer === editorDiv || editorDiv.contains(range.commonAncestorContainer))) {
            // 現在の選択範囲を削除
            range.deleteContents();
            
            // テキストノードを作成して挿入
            const textNode = document.createTextNode(timestampText);
            range.insertNode(textNode);
          } else {
            // 選択範囲がない場合は末尾に追加
            const textNode = document.createTextNode(timestampText);
            editorDiv.appendChild(textNode);
          }
          
          // 挿入後のカーソル位置を文字列位置として計算
          const newCursorPosition = currentCursorPosition + timestampText.length;
          
          // onChangeイベントを発火（RichCommentInputの状態を更新）
          const inputEvent = new Event('input', { bubbles: true });
          editorDiv.dispatchEvent(inputEvent);
          
          // inputイベント発火後、useEffectでDOM再構築された後にカーソル位置を復元
          setTimeout(() => {
            const restoredSelection = {
              start: newCursorPosition,
              end: newCursorPosition
            };
            restoreSelection(editorDiv, restoredSelection);
          }, 50);
        }
      }
    }
  };

  // シーク操作
  const handleSeekBackward = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(currentTime - 10, 0), 'seconds');
    }
  };
  const handleSeekForward = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      const currentDuration = typeof playerRef.current.getDuration === 'function' 
        ? playerRef.current.getDuration() 
        : duration;
      playerRef.current.seekTo(Math.min(currentTime + 10, currentDuration), 'seconds');
    }
  };

  // プレーヤーのシーク処理をuseCallbackでメモ化
  const handleSeekToTime = useCallback((sec: number) => {
    if (playerRef.current) {
      playerRef.current.seekTo(sec, 'seconds');
    }
  }, []);

  // 投稿者情報・タグなどの派生データ
  const uploaderIconUrl = video?.uploader?.icon_path
    ? toRelativePath(video.uploader.icon_path)
    : '/asset/no_icon_user.png';
  const hospitalName = video?.uploader?.hospital?.name || '病院不明';
  const uploaderName = video?.uploader?.name || '名無しユーザー';
  const tagArray = video?.tags ? video.tags.split(',') : [];
  const hasImages = !!(video && video.images && video.images.length > 0);

  // オーナー判定
  const activeSession = SessionManager.getActiveSession();
  const activeUserId = activeSession ? activeSession.userId : null;
  const isOwner = useMemo(() => {
    return !!activeUserId && !!video && video.uploader?.id === activeUserId;
  }, [activeUserId, video]);

  // ダウンロードハンドラー
  const handleDownload = useCallback(async (): Promise<void> => {
    if (!video || !video.id) {
      showSnackbar('動画情報が取得できません', SnackbarType.ERROR);
      return;
    }

    const activeSession = SessionManager.getActiveSession();
    const token = activeSession ? activeSession.token : null;
    const defaultFileName = video.title || video.name || 'video';

    await downloadVideo(
      video.id,
      token || '',
      defaultFileName,
      undefined,
      (errorMessage) => {
        showSnackbar(errorMessage, SnackbarType.ERROR);
      },
      {
        startDownloadProgress,
        updateDownloadProgress,
        completeDownloadProgress
      }
    );
  }, [video, showSnackbar, startDownloadProgress, updateDownloadProgress, completeDownloadProgress]);

  return {
    // 基本データ
    video,
    error,
    isConverting,
    isDeleted,
    
    // プレイヤー関連
    playerRef,
    duration,
    lastWatchSeconds,
    isMuted,
    
    // いいね・お気に入り関連
    hasLiked,
    likesCount,
    hasFavorited,
    likers,
    likeDialogOpen,
    
    // 再生位置・視聴記録
    viewCounted,
    
    // メタデータ
    videoMetadataRef,
    tagArray,
    hospitalName,
    uploaderName,
    uploaderIconUrl,
    hasImages,
    
    // ハンドラー関数
    handlePlayerReady,
    handleDuration,
    handleProgress,
    handleEnded,
    handleTagClick,
    handleLike,
    handleFavorite,
    handleShowLikers,
    closeLikersDialog,
    handleComment,
    handleCommentWithTime,
    handleSeekBackward,
    handleSeekForward,
    handleSeekToTime,
    handleToggleMute,
    handleAutoPlayError,
    isOwner,
    handleDownload
  };
}; 
